<template>
  <div class="fill-height pa-0 ma-0" style="background-color:#f5f5f5" fluid>
    <v-dialog max-width="400" v-model="newClusterDialog">
      <v-card flat>
        <v-card-title>Create a new Cluster
          <div class="flex-grow-1"></div> <v-btn icon @click="newClusterDialog = !newClusterDialog"><v-icon
              medium>mdi-close</v-icon></v-btn>
        </v-card-title>
        <v-divider></v-divider>
        <v-text-field v-model="newClusterName" solo-inverted hide-details flat label="New cluster name..."
          class="pa-5"></v-text-field>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="danger" text @click="postCluster()">
            Save
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <div>
      <v-row justify="center" class="px-0 py-3">
        <v-card color="#f5f5f5" flat width="95%" class="mx-auto">
          <p style="text-align:center;" class="pt-6 display-1 font-weight-medium">
            <span v-if="entityType == 'supplier'">Supplier Clusters</span>
            <span v-else>Buyer Clusters</span>
          </p>
          <p style="text-align:center;" class="body-1 font-weight-normal">
            Use Clusters to build a single view of multiple related supplier or buyer entities. Common examples include
            competitors, subsidiaries, government regions or verticals, and business partners.
          </p>

          <v-card flat>
            <v-data-table :headers="headers" :items="filteredClusters" item-selected-class="" :footer-props="{
              'items-per-page-options': [100]
            }" :items-per-page="100" :loading="loading" class="elevation-1 py-2">
              <!-- top slot -->
              <template v-slot:top>
                <v-card-title>
                  <v-select
                    v-model="selectedClusterTypeFilter"
                    :items="clusterTypes"
                    item-text="name"
                    item-value="id"
                    dense
                    outlined
                    hide-details
                    clearable
                    label="Cluster Type"
                    class="pr-3 py-3"
                    style="max-width: 250px;"
                  >
                    <template v-slot:prepend-inner>
                      <v-icon>mdi-filter</v-icon>
                    </template>
                  </v-select>
                  <v-text-field prepend-inner-icon="mdi-magnify" v-model="search" dense single-line outlined
                    hide-details clearable flat label="Search" class="pr-3 py-3"></v-text-field>
                  <v-btn outlined @click="newClusterDialog = true"><v-icon>mdi-plus</v-icon> Cluster</v-btn>
                </v-card-title>
              </template>
              <!-- name column -->
              <template v-slot:item.name="{ item }">
                <div class="d-flex align-center">
                  <template v-if="!item.__editing">
                    <!-- Name as router link (navigates on click) -->
                    <router-link
                      :to="`/cluster/${item.id}`"
                      style="text-decoration: none; color: inherit;"
                      class="flex-grow-1"
                    >
                      <span class="hvr">{{ item.name }}</span>
                    </router-link>
                    
                    <!-- pencil icon -->
                    <v-btn icon class="ml-2" @click.stop="startEdit(item)">
                      <v-icon small>mdi-pencil</v-icon>
                    </v-btn>
                  </template>

                  <!-- Edit dialog with pencil activator  -->
                  <template v-else>
                    <v-text-field
                      v-model="item.__draftName"
                      single-line
                      dense
                      hide-details
                      :maxlength="200"
                      class="flex-grow-1"
                    />
                    
                    <!-- Save button with loading indicator -->
                    <v-btn
                      icon color="primary" class="ml-1"
                      :loading="!!item.__saving"
                      :disabled="!!item.__saving"
                      @click.stop="saveEdit(item)"
                    >
                      <v-icon small v-if="!item.__saving">mdi-check</v-icon>
                    </v-btn>

                    <v-btn icon class="ml-1" :disabled="!!item.__saving" @click.stop="cancelEdit(item)">
                      <v-icon small>mdi-close</v-icon>
                    </v-btn>
                  </template>
                </div>
                  <!-- optional subtitle for entity count (unchanged) -->
                  <!-- <v-list-item-subtitle v-if="item.search_query">
                        {{ JSON.parse(item.search_query)[entityIDType].length }} entities
                      </v-list-item-subtitle> -->
              </template>
              <template slot="item.cluster_type" slot-scope="{ item }">
                <v-select
                  v-model="item.cluster_type_id"
                  :items="clusterTypes"
                  item-text="name"
                  item-value="id"
                  dense
                  hide-details
                  @change="updateClusterType(item)"
                  :loading="item.saving"
                  :disabled="item.saving"
                  class="cluster-type-select"
                >
                  <template v-slot:selection="{ item: selectedItem }">
                    <span>{{ selectedItem.name }}</span>
                  </template>
                </v-select>
              </template>
              <template slot="item.entity_count" slot-scope="{ item }">
                <span v-if="item.search_query">{{ JSON.parse(item.search_query)[entityIDType].length }}</span>
              </template>
              <template slot="item.id" slot-scope="{ item }">
                <div>
                  <v-tooltip top dark color="#000000" style="opacity: 0.7;">
                    <template v-slot:activator="{ on }">
                      <v-btn v-on="on" icon target="_blank" color="secondary" light
                        style="text-decoration: none !important;" @click="fetchClusterData(item.id)">
                        <v-icon>mdi-format-list-bulleted</v-icon>
                      </v-btn>
                    </template>
                    <span>View all {{ item.name }} notices</span>
                  </v-tooltip>

                  <v-tooltip top dark color="#000000" style="opacity: 0.7;">
                    <template v-slot:activator="{ on }">
                      <v-btn right color="secondary" icon target="_blank" :to="'/cluster/' + item.id" v-on="on">
                        <v-icon medium color="secondary">mdi-open-in-new</v-icon>
                      </v-btn>
                    </template>
                    <span>View / Edit {{ item.name }} Cluster Profile</span>
                  </v-tooltip>

                  <v-tooltip top dark color="#000000" style="opacity: 0.7;">
                    <template v-slot:activator="{ on }">
                      <v-btn right icon @click="selectedItem = item, delete_cluster = true" v-on="on">
                        <v-icon medium color="primary">mdi-delete</v-icon>
                      </v-btn>
                    </template>
                    <span>Delete {{ item.name }} Cluster</span>
                  </v-tooltip>
                </div>
              </template>
            </v-data-table>
          </v-card>
        </v-card>
      </v-row>
    </div>
    <v-dialog v-model="delete_cluster" max-width="400" absolute>
      <v-card>
        <p class="font-weight-bold mx-auto pa-6">
          <span class="text-body-1">Delete Cluster: </span>
          <span v-if="selectedItem"> {{ selectedItem["name"] }}</span><span class="text-body-1">?</span>
        </p>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="secondary" text @click="delete_cluster = false">
            Cancel
          </v-btn>

          <v-btn color="primary" text @click="(delete_cluster = false), removeCluster(selectedItem.id)">
            Delete
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    
    <!-- Success/Error Notifications -->
    <v-snackbar v-model="notification.show" color="primary" top :timeout="3000">
      {{ notification.message }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="notification.show = false">
          Close
        </v-btn>
      </template>
    </v-snackbar>

    <!-- Duplicate cluster name validation -->
    <v-snackbar v-model="duplicateErrorSnackbar" color="error" class="duplicate-error-snackbar">
      A cluster with this name already exists.
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="duplicateErrorSnackbar = false">Close</v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import { getClusters, updateClusters, addCluster, deleteCluster, getClusterTypes } from "../components/APIManager.js";
import { bus } from "../main.js";

export default {
  data() {
    return {
      newClusterDialog: false,
      newClusterName: "",
      search: "",
      selectedClusterTypeFilter: null,
      clusters: [],
      clusterTypes: [],
      keywords: [],
      loading: false,
      selectedItem: null,
      delete_cluster: false,
      edit_cluster: false,
      notification: {
        show: false,
        message: ''
      },
      duplicateErrorSnackbar: false,
      //entityIDType: "publisher_id"
    };
  },

  watch: {
    $route(to, from) {
      this.loading = true;
      let qry = JSON.parse(JSON.stringify(this.$route.query));
      this.loadClusters(qry.entity_type);
    },

    /* delete_cluster(value) {
      if (!value) {
        this.loadClusters();
      } 
    }*/
  },

  mounted() {
    this.loadClusters(this.entityType);
    this.loadClusterTypes();
  },

  computed: {
    headers() {
      return [
        { text: "Cluster Name", value: "name" },
        { text: "Cluster Type", value: "cluster_type" },
        { text: "Number of Entities", value: "entity_count" },
        { text: "Actions", value: "id" },
      ];
    },
    entityIDType() {
      let qry = JSON.parse(JSON.stringify(this.$route.query));

      return qry.entity_type + "_id";
    },

    entityType() {

      let qry = JSON.parse(JSON.stringify(this.$route.query));

      return qry.entity_type || 'publisher';
    },

    filteredClusters() {
      return this.clusters.filter(cluster => {
        // Filter by search text
        const matchesSearch = cluster.name.toLowerCase().includes(this.search.toLowerCase());
        
        // Filter by cluster type (if a filter is selected)
        const matchesClusterType = !this.selectedClusterTypeFilter || 
          cluster.cluster_type_id === this.selectedClusterTypeFilter;
        
        return matchesSearch && matchesClusterType;
      });
    },

    isDuplicateClusterName() {
      const name = (this.newClusterName || '').trim();
      if (!name) return false;
      const clusters = Array.isArray(this.clusters) ? this.clusters : [];
      const lower = name.toLowerCase();
      return clusters.some(c => c && (c.name || '').toString().toLowerCase() === lower);
    }
  },

  methods: {

    async postCluster() {
      if (!this.newClusterName.trim()) {
        return; // Don't create cluster with empty name
      }

      const name = this.newClusterName.trim();
      const clusters = Array.isArray(this.clusters) ? this.clusters : [];
      const isDuplicate = clusters.some(c => c && (c.name || '').toString().toLowerCase() === name.toLowerCase());
      if (isDuplicate) {
        this.duplicateErrorSnackbar = true;
        return;
      }
      
      this.loading = true;
      const clusterName = this.newClusterName;
      
      try {
        const results = await addCluster(encodeURIComponent(clusterName), this.entityType);
        this.clusters = results.data;
        this.newClusterName = "";
        this.loading = false;
        
        // Close the dialog
        this.newClusterDialog = false;
        
        // Find the newly created cluster and navigate to it
        const newCluster = results.data.find(cluster => 
          cluster.name === clusterName
        );
        
        if (newCluster) {
          // Navigate to the new cluster page
          this.$router.push(`/cluster/${newCluster.id}`);
        }
      } catch (error) {
        console.error('Error creating cluster:', error);
        this.loading = false;
        this.showNotification('Failed to create cluster');
      }
    },

    async removeCluster(id) {
      this.loading = true;
      deleteCluster(id).then(results => {
        this.clusters = [];
        this.clusters = results.data;
        this.loading = false;
      });
    },

    async loadClusters(clusterType) {
      this.loading = true;
      getClusters(clusterType).then(results => {
        const data = results && results.data !== undefined ? results.data : results;
        this.clusters = Array.isArray(data) ? data : [];
        this.loading = false;
      });
    },

    clearSearch() {
      this.keywords = [];
      this.loadClusters();
    },

    remove_kw(item) {
      this.keywords.splice(this.keywords.indexOf(item), 1);
      if (this.keywords.length == 0) {
        this.clearSearch();
      }
    },

    async loadClusterTypes() {
      try {
        const response = await getClusterTypes();
        this.clusterTypes = response.data || [];
      } catch (error) {
        console.error('Error loading cluster types:', error);
      }
    },

    getClusterTypeName(clusterTypeId) {
      if (!clusterTypeId || !this.clusterTypes.length) {
        return 'Unknown';
      }
      const clusterType = this.clusterTypes.find(ct => ct.id === clusterTypeId);
      return clusterType ? clusterType.name : 'Unknown';
    },

    startEdit(cluster) {
      this.$set(cluster, '__editing', true);
      this.$set(cluster, '__draftName', cluster.name);
      this.$set(item, '__saving', false);
    },
    
    cancelEdit(item) {
      item.__editing = false;
      item.__draftName = '';
      item.__saving = false;
    },

    async saveEdit(item) {
      const newName = (item.__draftName || '').trim();
      if (!newName || newName === item.name) {
        item.__editing = false;
        return;
      }

      item.__saving = true;

      try {
        // Optimistic update
        const original = item.name;
        item.name = newName;

        await this.updateClusterName({ ...item, name: newName });
        item.__editing = false;
      } catch (error) {
        // revert on failure
        item.name = original;
        this.$toast.error('Failed to save name')
        console.error('Error saving cluster name:', error);
      } finally {
        item.__saving = false;
      }

    },

    async updateClusterType(cluster) {
      // Don't save if it's the same as the current value or if we're still loading
      if (!cluster.cluster_type_id || cluster.saving) {
        return;
      }

      // Set loading state
      this.$set(cluster, 'saving', true);
      
      try {
        // Update the cluster with the new cluster type
        const updateData = {
          id: cluster.id,
          name: cluster.name,
          entity_type: cluster.entity_type,
          cluster_type_id: cluster.cluster_type_id,
          owner_id: cluster.owner_id || 1 // Default owner if not set
        };

        const response = await fetch(process.env.VUE_APP_API_CLUSTER, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${await this.$auth.getTokenSilently()}`
          },
          body: JSON.stringify(updateData)
        });

        if (response.ok) {
          // Show success notification
          this.showNotification('Cluster type updated successfully');
        } else {
          // Revert the selection if the save failed
          const originalCluster = this.clusters.find(c => c.id === cluster.id);
          if (originalCluster) {
            cluster.cluster_type_id = originalCluster.cluster_type_id;
          }
          throw new Error('Failed to update cluster type');
        }
      } catch (error) {
        console.error('Error updating cluster type:', error);
        this.showNotification('Failed to update cluster type');
        // Revert the selection on error
        const originalCluster = this.clusters.find(c => c.id === cluster.id);
        if (originalCluster) {
          cluster.cluster_type_id = originalCluster.cluster_type_id;
        }
      } finally {
        this.$set(cluster, 'saving', false);
      }
    },

    async updateClusterName(cluster) {
      // Prevent empty names or concurrent saves
      if (!cluster.name || !cluster.name.trim() || cluster.savingName) {
        // If name is empty, revert to original from list
        const original = this.clusters.find(c => c.id === cluster.id);
        if (original) cluster.name = original.name;
        return;
      }

      this.$set(cluster, 'savingName', true);

      try {
        const updateData = {
          id: cluster.id,
          name: cluster.name,
          entity_type: cluster.entity_type,
          cluster_type_id: cluster.cluster_type_id,
          owner_id: cluster.owner_id || 1
        };

        const response = await fetch(process.env.VUE_APP_API_CLUSTER, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${await this.$auth.getTokenSilently()}`
          },
          body: JSON.stringify(updateData)
        });

        if (response.ok) {
          this.showNotification('Cluster name updated');
        } else {
          // revert on failure
          const original = this.clusters.find(c => c.id === cluster.id);
          if (original) cluster.name = original.name;
          this.showNotification('Failed to update cluster name');
        }
      } catch (error) {
        console.error('Error updating cluster name:', error);
        const original = this.clusters.find(c => c.id === cluster.id);
        if (original) cluster.name = original.name;
        this.showNotification('Failed to update cluster name');
      } finally {
        this.$set(cluster, 'savingName', false);
      }
    },

    showNotification(message) {
      this.notification.message = message;
      this.notification.show = true;
    },

    fetchClusterData(cluster_id) {
      const qry = JSON.parse(JSON.stringify(this.$route.query));
      const entityType = qry.entity_type;
      const cluster_filter_name = entityType === "supplier" ? "supplier_cluster" : "buyer_cluster";

      this.$router.push({
        name: "search",
        query: {
          [cluster_filter_name]: [cluster_id],
          sort_order: "asc",
          viewIndex: 0
        }
      });
      bus.$emit("hide-side-navs", true);
    },
  }
};
</script>

<style scoped>
.v-application--is-ltr .v-list-group--sub-group .v-list-group__header {
  padding-left: 0px !important;
}

.wrap-text {
  -webkit-line-clamp: unset !important;
}

.tooltip {
  display: inline-block;
  max-width: 360px;
}

.border {
  border: 1px solid rgb(255, 20, 99);
}

.hvr:hover {
  text-decoration: underline !important;
}

.cluster-type-select {
  max-width: 200px;
}

.cluster-type-select .v-input__control {
  min-height: 36px !important;
}

.duplicate-error-snackbar.v-snack,
.duplicate-error-snackbar >>> .v-snack__wrapper {
  z-index: 9999;
}
</style>